import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Carousel, Col, Modal, Row, message } from "antd";
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ArrowRightOutlined } from "@ant-design/icons";
import { emptyImage, serverFetch } from "@extensions";
import "./home.css";
import DOMPurify from "dompurify";
import { useCompany } from "@redux";
import { useGetIsDesktop, useGetIsTablet } from "extensions";

const Home = () => {
  const emptyGroups = [{ id: 0, name: "", description: "" }];
  const navigate = useNavigate();
  const company = useCompany();
  const [groups, setGroups] = useState(emptyGroups);
  const [loading, setLoading] = useState(false);
  const carouselRef = useRef(null);
  const bannerDescriptionRef = useRef(null);
  const bannerDescriptionContentRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [isBannerTextOverflowing, setIsBannerTextOverflowing] = useState(false);
  const isDesktop = useGetIsDesktop();
  const isTablet = useGetIsTablet();
  const hasBannerLink = !!company?.adUrl;
  const backgroundStyle = company?.catalogBackgroundImage
    ? { backgroundImage: `url("${company.catalogBackgroundImage}")` }
    : undefined;

  const bannerDescriptionHtml = DOMPurify.sanitize(company?.description || "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["a", "object", "iframe"],
  });
  const bannerTitle = company?.bannerTitle || "Попробуйте демо-версию!";

  useEffect(() => {
    if (company && company.id > 0) {
      setLoading(true);
      let queryParams = {
        companyId: company.id,
        isActive: true,
      };
      serverFetch("Online/CatalogGroups", {
        method: "GET",
        queryParams: queryParams,
      })
        .then((data) => {
          setGroups(data);
          setLoading(false);
        })
        .catch((e) => {
          message.error("Ошибка получения групп каталога");
          setLoading(false);
        });
    }
  }, [company]);

  useEffect(() => {
    const bannerDescription = bannerDescriptionRef.current;
    const bannerDescriptionContent = bannerDescriptionContentRef.current;
    if (!bannerDescription || !bannerDescriptionContent) return undefined;

    const checkBannerOverflow = () => {
      setIsBannerTextOverflowing(
        bannerDescriptionContent.scrollHeight > bannerDescriptionContent.clientHeight ||
          bannerDescriptionContent.scrollWidth > bannerDescriptionContent.clientWidth ||
          bannerDescription.scrollHeight > bannerDescription.clientHeight ||
          bannerDescription.scrollWidth > bannerDescription.clientWidth
      );
    };

    checkBannerOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(checkBannerOverflow) : null;
    resizeObserver?.observe(bannerDescription);
    resizeObserver?.observe(bannerDescriptionContent);
    window.addEventListener("resize", checkBannerOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkBannerOverflow);
    };
  }, [bannerDescriptionHtml]);

  const openBannerModal = () => {
    if (isBannerTextOverflowing) {
      setIsBannerModalOpen(true);
    }
  };

  const openVisualBannerLink = () => {
    if (!company?.adUrl) return;

    const url = new URL(company.adUrl, window.location.origin);
    if (url.origin === window.location.origin) {
      navigate(`${url.pathname}${url.search}${url.hash}`);
      return;
    }

    window.location.href = company.adUrl;
  };

  const renderGroups = () => {
    if (!groups || groups.length === 0) return null;

    const carouselSettings = { arrows: false, dots: false, infinite: true,
      speed: 500, slidesToShow: isDesktop ? 3 : isTablet ? 2 : 1, slidesToScroll: 1,
      beforeChange: (_, next) => setCurrentSlide(next),
      responsive: [
        { breakpoint: 992, settings: { slidesToShow: 2 }},
        { breakpoint: 576, settings: { slidesToShow: 1 }}
      ]
    };

    return (
      <div className="home-carousel-wrapper">
        <button className="custom-glass-arrow prev-arrow" onClick={() => carouselRef.current?.prev()} type="button">
          <LeftOutlined />
        </button>
        <Carousel ref={carouselRef} {...carouselSettings}> 
          {groups.map((g) => (
            <div key={g.id} className="carousel-slide-item">
              <Card
                className="home-custom-card"
                hoverable
                loading={loading}
                cover={
                  <div className="card-image-wrapper">
                    <img
                      alt={g.name}
                      src={g.imageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${g.imageId}.jpg` : emptyImage}
                    />
                  </div>
                }
                onClick={() => navigate(`/catalog/${g.id}`)}
              >
                <div className="card-body-content">
                  <h3 className="card-title-text">{g.name}</h3>
                  <div className="card-footer-link">
                    <span>Подробнее о тарифах</span>
                    <ArrowRightOutlined className="arrow-icon" />
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </Carousel>
        <button className="custom-glass-arrow next-arrow" onClick={() => carouselRef.current?.next()} type="button">
          <RightOutlined />
        </button>
      </div>
    );
  };
  
  return (
    <div className="home-page-container" style={backgroundStyle}>
      <Row className="banner-row-section" gutter={{ xs: 0, md: 24 }} style={{marginLeft: "0px", marginRight: "0px"}}>
        <Col xs={24} md={10} lg={8} style={{paddingLeft: "0px"}}>
          <div
            className={`text-banner-card ${isBannerTextOverflowing ? "text-banner-card_clickable" : ""}`}
            onClick={openBannerModal}
            onMouseDown={(e) => {
              if (isBannerTextOverflowing) {
                e.preventDefault();
              }
            }}
            role={isBannerTextOverflowing ? "button" : undefined}
            tabIndex={isBannerTextOverflowing ? 0 : undefined}
            onKeyDown={(e) => {
              if (isBannerTextOverflowing && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                setIsBannerModalOpen(true);
              }
            }}
          >
            <h1 className="banner-main-title">{bannerTitle}</h1>
            <div
              ref={bannerDescriptionRef}
              className={`banner-description-text ${isBannerTextOverflowing ? "banner-description-text_overflowing" : ""}`}
            >
              {company ? (
                <div
                  ref={bannerDescriptionContentRef}
                  dangerouslySetInnerHTML={{
                    __html: bannerDescriptionHtml,
                  }}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </Col>

        <Col xs={24} md={14} lg={16} style={{paddingRight: "0px"}}>
          <div
            className={`visual-banner-card ${hasBannerLink ? "visual-banner-card_clickable" : ""}`}
            onClick={openVisualBannerLink}
            role={hasBannerLink ? "link" : undefined}
            tabIndex={hasBannerLink ? 0 : undefined}
            onKeyDown={(e) => {
              if (hasBannerLink && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                openVisualBannerLink();
              }
            }}
          >
            {isDesktop ? 
            <img src={company?.bannerImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${company.bannerImageId}.jpg` : "/images/clover.jpg"} className="banner-image" alt=""/>
            : 
            <img src={company?.mobileBannerImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${company.mobileBannerImageId}.jpg` : "/images/clover.jpg"} className="banner-image" alt=""/>
            }
          </div>
        </Col>
      </Row>

      {company?.showCatalog && (
        <div className="catalog-cards-section">
          {renderGroups()}
          <div className="custom-pagination-dots">
            {groups.map((group, index) => (
              <span
                key={group.id}
                className={`dot ${currentSlide === index ? "active" : ""}`}
                onClick={() => carouselRef.current?.goTo(index)}
              />
            ))}
          </div>
        </div>
      )}

      <Modal
        className="banner-description-modal"
        footer={null}
        open={isBannerModalOpen}
        onCancel={() => setIsBannerModalOpen(false)}
      >
        <div className="banner-description-modal-card">
          <h1 className="banner-main-title">{bannerTitle}</h1>
          <div
            className="banner-description-modal-content"
            dangerouslySetInnerHTML={{
              __html: bannerDescriptionHtml,
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Home;
